import {EventRegistrationFormGuestSubmitMutationVariables} from './__generated__/Event.graphql';
import useSubmitGuestRegistration from './useSubmitGuestRegistration';
import useForm, {FormState} from '../../hooks/useForm';
import useRecordFieldValidator from '../../hooks/useRecordFieldValidator';
import useFormValidators from '../../hooks/useRecordValidator';
import allowNonEmptyString from '../../validators/allowNonEmptyString';
import alwaysDeny from '../../validators/alwaysDeny';
import {ButtonColor, ButtonSize} from '../design_system/Button/options';
import FormTextInput from '../design_system/form/FormTextInput';
import FormRadioGroup from '../design_system/form/RadioGroup/FormRadioGroup';
import useAppTheme from '../design_system/hooks/useAppTheme';
import FormSubmitButton from '../Form/FormSubmitButton';
import SuccessOverlay from '../Image/SuccessOverlay';
import Flex from '../layout/flex/Flex';

import {css} from '@emotion/react';
import React, {useCallback} from 'react';
import {Trans, useTranslation} from 'react-i18next';

export interface Props {
  readonly eventID: string;
  readonly isDisabled?: boolean;
}

export default function RegistrationFormGuestViewer(props: Props): JSX.Element {
  const {eventID} = props;
  let {isDisabled} = props;
  const {t} = useTranslation();
  const {spacing} = useAppTheme();
  const doRegister = useSubmitGuestRegistration();
  const lastNameValidator = useRecordFieldValidator<
    EventRegistrationFormGuestSubmitMutationVariables,
    'lastName'
  >(
    'lastName',
    allowNonEmptyString(
      alwaysDeny(
        t('validation.missing-required-field', {
          name: t('pages.event.last-name'),
        }),
      ),
    ),
  );
  const firstNameValidator = useRecordFieldValidator<
    EventRegistrationFormGuestSubmitMutationVariables,
    'firstName'
  >(
    'firstName',
    allowNonEmptyString(
      alwaysDeny(
        t('validation.missing-required-field', {
          name: t('pages.event.first-name'),
        }),
      ),
    ),
  );
  const emailValidator = useRecordFieldValidator<
    EventRegistrationFormGuestSubmitMutationVariables,
    'email'
  >(
    'email',
    allowNonEmptyString(
      alwaysDeny(
        t('validation.missing-required-field', {
          name: t('email-credential.email-address'),
        }),
      ),
    ),
  );
  const companyValidator = useRecordFieldValidator<
    EventRegistrationFormGuestSubmitMutationVariables,
    'company'
  >(
    'company',
    allowNonEmptyString(
      alwaysDeny(
        t('validation.missing-required-field', {
          name: t('pages.event.company'),
        }),
      ),
    ),
  );
  const channelValidator = useRecordFieldValidator<
    EventRegistrationFormGuestSubmitMutationVariables,
    'channel'
  >(
    'channel',
    allowNonEmptyString(
      alwaysDeny(
        t('validation.missing-required-field', {
          name: t('pages.event.where-did-you-hear-about-us'),
        }),
      ),
    ),
  );
  const onValidate = useFormValidators([
    lastNameValidator,
    firstNameValidator,
    emailValidator,
    companyValidator,
    channelValidator,
  ]);
  const onSubmit = useCallback(
    async (input: EventRegistrationFormGuestSubmitMutationVariables) => {
      await doRegister(input);
    },
    [doRegister],
  );

  const formObject = useForm<EventRegistrationFormGuestSubmitMutationVariables>(
    {
      fallbackFormErrors: [t('form.unknown-form-submission-error')],
      onSubmit,
      onValidate,
      options: {
        forbidSubmissionWhenSuccess: true,
      },
      startingValue: {
        sessionID: eventID,
        email: '',
        lastName: '',
        firstName: '',
        company: '',
        channel: '',
      },
    },
  );

  const {formState} = formObject;
  const submitted = formState === FormState.SUBMITTED_SUCCESS;
  isDisabled = submitted || isDisabled;

  return (
    <form
      css={css({
        position: 'relative',
      })}
      onSubmit={(e) => {
        void formObject.doSubmit(e);
      }}
    >
      <Flex
        sx={css({
          flexDirection: 'column',
          gap: spacing.x24,
          alignItems: 'flex-start',
        })}
      >
        <Flex
          sx={css({
            alignItems: 'flex-start',
            gap: spacing.x24,
            width: '100%',
          })}
        >
          <FormTextInput
            formObject={formObject}
            getValue={(fieldValues) => fieldValues.lastName}
            isDisabled={isDisabled}
            isRequired={true}
            label={t('pages.event.last-name')}
            name="lastName"
            style={css({
              flex: 1,
            })}
          />
          <FormTextInput
            formObject={formObject}
            getValue={(fieldValues) => fieldValues.firstName}
            isDisabled={isDisabled}
            isRequired={true}
            label={t('pages.event.first-name')}
            name="firstName"
            style={css({
              flex: 1,
            })}
          />
        </Flex>

        <FormTextInput
          formObject={formObject}
          getValue={(fieldValues) => fieldValues.email}
          isDisabled={isDisabled}
          isRequired={true}
          label={t('email-credential.email-address')}
          name="email"
          style={css({
            width: '100%',
          })}
          type="email"
        />

        <FormTextInput
          formObject={formObject}
          getValue={(values) => values.company}
          isDisabled={isDisabled}
          isRequired={true}
          label={t('pages.event.company')}
          name="company"
          style={css({
            width: '100%',
          })}
          type="text"
        />

        <FormRadioGroup
          formObject={formObject}
          getValue={(values) => values.channel}
          isDisabled={isDisabled}
          isRequired={true}
          label={t('pages.event.where-did-you-hear-about-us')}
          name="channel"
          options={[
            {
              id: 'homepage',
              ariaLabel: t('common-strings.home-page'),
              label: t('common-strings.home-page'),
              value: 'homepage',
            },
            {
              id: 'sns',
              ariaLabel: t('pages.event.form-field-sns'),
              label: t('pages.event.form-field-sns'),
              value: 'sns',
            },
            {
              id: 'friends',
              ariaLabel: t('pages.event.friends'),
              label: t('pages.event.friends'),
              value: 'freinds',
            },
            {
              id: 'searchEngine',
              ariaLabel: t('pages.event.form-field-search-engine'),
              label: t('pages.event.form-field-search-engine'),
              value: 'searchEngine',
            },
            {
              id: 'others',
              ariaLabel: t('common-strings.others'),
              label: t('common-strings.others'),
              value: 'others',
            },
          ]}
        />

        <FormSubmitButton
          color={ButtonColor.ACCENT}
          formObject={formObject}
          idleLabel={t('pages.event.register-for-event')}
          isDisabled={isDisabled}
          progressLabel={t('form.submitting')}
          size={ButtonSize.LARGE}
          sx={css({
            width: '100%',
          })}
        />
      </Flex>
      {submitted && (
        <SuccessOverlay
          label={<Trans i18nKey="pages.event.registration-complete" />}
        />
      )}
    </form>
  );
}
